/**
 * Flex packs: the catalogue, the checkout, and what you have left.
 *
 * THE CLIENT NEVER NAMES A PRICE. It names a pack CODE (`flex_review_20`) and
 * the backend resolves that to the Stripe price it owns, creates the Checkout
 * session and hands back a URL. Card details, 3-D Secure and the PROMO CODE
 * box all live on Stripe's page - nothing here collects any of them, and
 * nothing here can be talked into a cheaper charge by editing a request.
 *
 * ENDPOINTS. The three below are the interface this surface needs. Where the
 * backend has not landed yet, each one degrades rather than breaks: the
 * catalogue falls back to the published price sheet, and the balances call
 * resolves to null so the surfaces that read it simply do not draw. The
 * checkout is the one that genuinely cannot be faked - see `createPackCheckout`.
 */

import apis from '../../utils/apis';
import { handleApiError } from './utils/utils';
import { PACKS, PackRow } from '../../config/pricing';

/* ------------------------------------------------------------------ */
/*  The catalogue                                                      */
/* ------------------------------------------------------------------ */

export interface PackSku {
  code: string;
  name: string;
  /** One-off price in cents. */
  price: number;
  currency: string;
  units: number;
  unitLabel: string;
  description: string;
  featured?: boolean;
  /** False when the backend has the SKU but cannot sell it right now. */
  purchasable?: boolean;
}

const fromSheet = (pack: PackRow): PackSku => ({
  code: pack.code,
  name: pack.name,
  price: pack.price,
  currency: 'EUR',
  units: pack.units,
  unitLabel: pack.unitLabel,
  description: pack.description,
  featured: pack.featured,
  purchasable: true,
});

/*
  The backend's `unit_type` (shared/pack_service.list_catalogue) against the
  sheet's `meter`, so a backend row can borrow the sheet's unit label and copy.
  Codes match the backend seed (pack_sku.code); unit_type stays the join so a
  backend-only SKU still gets copy.
*/
const UNIT_TYPE_TO_METER: Record<string, string> = {
  review: 'review_runs',
  table: 'table_runs',
  generation: 'generations',
  research: 'queries',
  matter_analysis: 'matter_analyses',
};

/** Whatever shape the catalogue arrives in, reduced to the one we render. */
const normalisePack = (raw: any): PackSku | null => {
  const code = String(raw?.code || raw?.pack_code || raw?.sku || '').trim();
  if (!code) return null;
  const meter = UNIT_TYPE_TO_METER[String(raw?.unit_type || '')];
  const sheet =
    PACKS.find((pack) => pack.code === code) ||
    (meter ? PACKS.find((pack) => pack.meter === meter) : undefined);
  /* `/payments/packs/catalogue` prices in EUROS (`price_eur: 29.0`); the UI
     works in cents. */
  const cents =
    raw?.price_cents ??
    (raw?.price_eur !== undefined && raw?.price_eur !== null
      ? Math.round(Number(raw.price_eur) * 100)
      : undefined) ??
    raw?.price ??
    sheet?.price ??
    0;
  return {
    code,
    /* The sheet's name ("Review runs") before the backend's display name
       ("20 review runs"): it is a translation key, and the card already shows
       the unit count beside it. */
    name: String(raw?.name || sheet?.name || raw?.display_name || code),
    price: Number(cents),
    currency: String(raw?.currency || 'EUR').toUpperCase(),
    units: Number(raw?.units ?? raw?.unit_count ?? sheet?.units ?? 0),
    unitLabel: String(raw?.unit_label || raw?.unitLabel || sheet?.unitLabel || 'units'),
    description: String(raw?.description || sheet?.description || ''),
    featured: raw?.featured ?? sheet?.featured,
    purchasable: raw?.purchasable !== false,
  };
};

/**
 * GET /payments/packs/catalogue - the sellable pack catalogue.
 *
 * Answers `{ success, packs: [{ code, display_name, kind, unit_type, units,
 * price_eur, currency, purchasable, requires_scope }] }`. If it fails or is
 * empty, this returns the published price sheet, which is correct to quote
 * from; a checkout against a sheet SKU the backend does not sell fails at the
 * checkout call, where there is a real error to show.
 *
 * Project packs (`requires_scope`) are left out: they must be bound to a
 * matter or project at purchase, and this surface has no way to choose one,
 * so offering them here would only produce a checkout the backend refuses.
 */
export const fetchPackCatalogue = () => async (dispatch: any): Promise<PackSku[]> => {
  try {
    const resp = await apis({ method: 'GET', url: '/payments/packs/catalogue' });
    const rows: any[] = resp?.data?.packs || resp?.data?.data || resp?.data || [];
    const packs = (Array.isArray(rows) ? rows : [])
      .filter((row) => !row?.requires_scope)
      .map(normalisePack)
      .filter((pack): pack is PackSku => !!pack);
    if (packs.length) return packs;
  } catch {
    // Falls through to the sheet. Deliberately silent: a customer looking at
    // the pack list must not be shown a network error about a catalogue they
    // do not know exists.
  }
  return PACKS.map(fromSheet);
};

/* ------------------------------------------------------------------ */
/*  The purchase                                                       */
/* ------------------------------------------------------------------ */

export interface PackCheckoutSession {
  url: string;
  session_id?: string;
}

/**
 * POST /payments/packs/checkout - create a one-off Stripe Checkout session.
 *
 * THE ONE CALL THAT CANNOT DEGRADE. Everything else on this surface has a
 * sensible offline answer; a payment does not. If this fails the user is told,
 * and stays where they are with nothing bought.
 *
 * `allow_promotion_codes` is the backend's decision, not ours - we ask for a
 * session and Stripe's page shows the promo box when the session was created
 * with it enabled. Sending a promo code from here would be asking the client
 * to validate a discount, which is the client deciding what to charge.
 */
export const createPackCheckout = (params: {
  pack_code: string;
  quantity?: number;
  success_url: string;
  cancel_url: string;
}) => async (dispatch: any): Promise<PackCheckoutSession | null> => {
  try {
    const resp = await apis({
      method: 'POST',
      url: '/payments/packs/checkout',
      data: {
        pack_code: params.pack_code,
        quantity: Math.max(1, Number(params.quantity || 1)),
        success_url: params.success_url,
        cancel_url: params.cancel_url,
      },
    });
    const url = resp?.data?.url || resp?.data?.checkout_url;
    if ((resp?.status === 200 || resp?.status === 201) && url) {
      return { url: String(url), session_id: resp.data?.session_id };
    }
    handleApiError(resp, dispatch);
    return null;
  } catch (err) {
    handleApiError(err, dispatch);
    return null;
  }
};

/* ------------------------------------------------------------------ */
/*  What the firm has left                                             */
/* ------------------------------------------------------------------ */

export interface UnitBalance {
  /** `review`, `table`, `generation`, `research`, `matter_analysis`, `ask`. */
  unit_type: string;
  label: string;
  /** Pooled allowance for the period, from seats and modules. */
  allowance: number;
  /** Consumed against the allowance this period. */
  used: number;
  /** Prepaid pack units left. These never expire. */
  pack_balance: number;
  /**
   * Euro fields - present ONLY on the dev resource environment
   * (`cost_display` true). The server omits them entirely on production, so
   * their absence is the signal to render no money, not a zero.
   */
  unit_price_eur?: number;
  value_used_eur?: number;
  value_included_eur?: number;
}

export interface AllowanceSummary {
  period_start?: string | null;
  period_end?: string | null;
  balances: UnitBalance[];
  /** True only on dev.pons.io / demo.pons.io. Gates all euro display. */
  cost_display: boolean;
  currency: string;
  /** Sum of value_used_eur across units. Present only when cost_display. */
  total_value_used_eur?: number;
  /** `{unit_type: eur}` - present only when cost_display. */
  unit_prices?: Record<string, number>;
  /** `{run_type: unit_type}` - lets the usage panel value per-run rows. */
  unit_type_by_run_type?: Record<string, string>;
}

const normaliseBalance = (raw: any): UnitBalance | null => {
  const unitType = String(raw?.unit_type || raw?.type || '').trim();
  if (!unitType) return null;
  const out: UnitBalance = {
    unit_type: unitType,
    label: String(raw?.label || raw?.name || unitType.replace(/_/g, ' ')),
    allowance: Number(raw?.allowance ?? raw?.included ?? 0),
    used: Number(raw?.used ?? raw?.consumed ?? 0),
    pack_balance: Number(raw?.pack_balance ?? raw?.packs ?? raw?.prepaid ?? 0),
  };
  if (raw?.unit_price_eur != null) out.unit_price_eur = Number(raw.unit_price_eur);
  if (raw?.value_used_eur != null) out.value_used_eur = Number(raw.value_used_eur);
  if (raw?.value_included_eur != null) out.value_included_eur = Number(raw.value_included_eur);
  return out;
};

/**
 * GET /payments/packs/allowance-usage - consumption against the pooled
 * allowance, prepaid pack units, and (dev resource env only) the euro value.
 *
 * Returns null when the backend has nothing to say, so every caller draws
 * nothing rather than drawing zeroes - a firm shown "0 of 0 used" would read it
 * as a limit reached, which is worse than showing no block at all. Euro fields
 * are present only when `cost_display` is true (the server decides, from
 * ENVIRONMENT); production never ships a money figure.
 */
export const fetchUnitBalances = () => async (): Promise<AllowanceSummary | null> => {
  try {
    const resp = await apis({ method: 'GET', url: '/payments/packs/allowance-usage' });
    if (resp?.status !== 200 || !resp.data) return null;
    const rows: any[] = resp.data?.balances || resp.data?.data || [];
    const balances = (Array.isArray(rows) ? rows : [])
      .map(normaliseBalance)
      .filter((row): row is UnitBalance => !!row);
    if (!balances.length) return null;
    return {
      period_start: resp.data?.period_start ?? null,
      period_end: resp.data?.period_end ?? null,
      balances,
      cost_display: Boolean(resp.data?.cost_display),
      currency: String(resp.data?.currency || 'EUR'),
      total_value_used_eur: resp.data?.total_value_used_eur != null
        ? Number(resp.data.total_value_used_eur) : undefined,
      unit_prices: resp.data?.unit_prices || undefined,
      unit_type_by_run_type: resp.data?.unit_type_by_run_type || undefined,
    };
  } catch {
    return null;
  }
};
